// src/pages/quality/QualityBoard.jsx
import { useEffect, useMemo, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";

import PageHeader from "../../components/common/ui/PageHeader";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";

import { getQualityMetaList, deleteQualityMeta } from "../../api/quality/meta";
import { fetchUserDetail } from "../../api/users/user";

// -------------------- 기존 상수 유지 --------------------
const LABELS = {
  INSPECTION: "검측요청서",
  CHECKLIST: "검측 CHECKLIST",
  NCR: "부적합/시정조치",
};
const ALL_TYPES = ["INSPECTION", "CHECKLIST", "NCR"];
const FORM_TYPE_TO_BOARD_TYPE = {
  MI: "INSPECTION",
  PC: "CHECKLIST",
  NC: "NCR",
  CA: "NCR",
};
const BOARD_TYPE_TO_FORM_TYPES = {
  INSPECTION: ["MI"],
  CHECKLIST: ["PC"],
  NCR: ["NC", "CA"],
};

const statusColor = (status) => {
  switch (status) {
    case "submit":
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const statusText = (s) =>
({
  none: "초안",
  submit: "상신",
  pending: "진행중",
  approved: "승인",
  rejected: "반려",
  cancel: "취소",
}[s] ?? "-");

// -------------------- UI 헬퍼 --------------------
const pickTitleFromMeta = (meta) => {
  if (!meta) return "";
  const form = meta.form || {};

  if (form.title?.trim()) return form.title.trim();

  const om = meta.officialMeta || {};
  const mname = om.material_name?.trim();
  const spec = om.spec?.trim();
  const dnno = om.delivery_note_no?.trim();

  if (mname && spec && dnno) return `[검수] ${mname} (${spec}) - ${dnno}`;
  if (mname && spec) return `[검수] ${mname} (${spec})`;
  if (mname) return `[검수] ${mname}`;

  return meta.__title__ || "품질 문서";
};

const pickDocDate = (meta) => {
  const f = meta?.form || {};
  return (
    f.inspection_datetime ||
    f.doc_datetime ||
    f.datetime ||
    f.inspection_date ||
    f.doc_date ||
    f.date ||
    ""
  );
};

const pickFormType = (meta) => {
  const raw =
    meta?.__client__?.form_type || meta?.form_type || meta?.doc_type || "";
  return String(raw).toUpperCase();
};

const formatName = (u) => {
  if (!u) return "";
  const ln = u.last_name?.trim() || "";
  const fn = u.first_name?.trim() || "";
  if (ln || fn) return `${ln}${fn}`;
  return u.name || u.username || "";
};

const formatDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// -------------------- Main --------------------
export default function QualityBoard() {
  const { id: projectId, boardType } = useParams();
  const normalized = (boardType || "all").toUpperCase();

  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();

  // -------------------- 상태 --------------------
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [userCache, setUserCache] = useState({});

  const [types, setTypes] = useState(() => {
    if (normalized === "ALL") return ALL_TYPES;
    if (ALL_TYPES.includes(normalized)) return [normalized];
    return ALL_TYPES;
  });

  // ✅ 게시판(라우트) 이동 시 컴포넌트가 재마운트되지 않으면
  //    types가 이전 값으로 남아 목록이 갱신되지 않을 수 있음.
  //    boardType 변경에 맞춰 state를 동기화하고 즉시 재조회한다.
  useEffect(() => {
    setPage(1);
    setQ("");
    setSearchField("title");
    setResult("");
    setDateFilter("all");
    setStartDate("");
    setEndDate("");
    setOrdering("-created_at");

    if (normalized === "ALL") setTypes(ALL_TYPES);
    else if (ALL_TYPES.includes(normalized)) setTypes([normalized]);
    else setTypes(ALL_TYPES);

    setSearchTrigger((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardType, location.pathname]);

  // -------------------- 검색/정렬 --------------------
  const [result, setResult] = useState(sp.get("result") || "");
  const [searchField, setSearchField] = useState(
    sp.get("searchField") || "title"
  );
  const [q, setQ] = useState(sp.get("q") || "");
  const [ordering, setOrdering] = useState(sp.get("ordering") || "-created_at");
  const [startDate, setStartDate] = useState(sp.get("start_date") || "");
  const [endDate, setEndDate] = useState(sp.get("end_date") || "");
  const [dateFilter, setDateFilter] = useState(sp.get("dateFilter") || "all");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const SEARCH_FIELDS = [
    { value: "title", label: "제목" },
    { value: "author", label: "작성자" },
    { value: "dns", label: "DNS" },
  ];

  // -------------------- form_type 필터 --------------------
  const formTypeFilter = useMemo(() => {
    const list = [];
    types.forEach((t) => {
      const mapped = BOARD_TYPE_TO_FORM_TYPES[t];
      if (mapped) list.push(...mapped);
    });
    return Array.from(new Set(list));
  }, [types]);

  const docTypeCsv = formTypeFilter.join(",");


  // -------------------- 유저 캐시 로딩 --------------------
  const ensureUsers = async (ids) => {
    const need = ids.filter((id) => userCache[id] == null);
    if (!need.length) return;

    const fetched = await Promise.allSettled(
      need.map((id) => fetchUserDetail(id))
    );

    const next = { ...userCache };
    fetched.forEach((res, idx) => {
      if (res.status === "fulfilled" && res.value) {
        next[need[idx]] = res.value;
      }
    });
    setUserCache(next);
  };

  // -------------------- 목록 로딩 --------------------
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = {
          project: projectId,
          page,
          page_size: pageSize,
          doc_type: docTypeCsv || undefined,
          result: result || undefined,
          ordering: ordering || undefined,
        };

        if (q) params[searchField] = q;

        if (dateFilter !== "all") {
          if (startDate) params.start_date = startDate;
          if (endDate) params.end_date = endDate;
        }

        const res = await getQualityMetaList(params);

        console.log(res)

        const items = Array.isArray(res) ? res : res?.results || [];

        let mapped = items.map((it) => {
          const meta = it.meta || {};
          const doc = it.doc || {};

          const formType = pickFormType(meta);
          const boardType = FORM_TYPE_TO_BOARD_TYPE[formType] || "INSPECTION";

          const title =
            doc.title?.trim() || pickTitleFromMeta(meta) || "(제목 없음)";

          const docDate = pickDocDate(meta);

          const createdByObj = doc.created_by || it.created_by;
          const createdById =
            createdByObj?.id ||
            createdByObj?.user_id ||
            (typeof createdByObj === "number" ? createdByObj : null);

          return {
            doc_dns: it.doc_dns || it.dns,
            title,
            created_by_id: createdById,
            created_at: doc.created_at || it.created_at,
            created_by_name: formatName(createdByObj),
            status: doc.status || it.status || "none",
            doc_type: boardType,
            raw_form_type: formType,
            doc_date: docDate || "",
            result: meta.form?.result || meta.result || "",
          };
        });

        const missing = mapped
          .filter((m) => m.created_by_id && !m.created_by_name)
          .map((m) => m.created_by_id);

        await ensureUsers(missing);

        mapped = mapped.map((m) => ({
          ...m,
          created_by_name:
            m.created_by_name || formatName(userCache[m.created_by_id]) || "-",
        }));

        const filterSet = new Set(types);
        mapped = mapped.filter((r) => filterSet.has(r.doc_type));

        setRows(mapped);
        setCount(res.total ?? 0);
      } catch {
        setError("목록을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) load();
  }, [
    projectId,
    page,
    pageSize,
    docTypeCsv,
    result,
    ordering,
    searchTrigger,
    types,
    startDate,
    endDate,
    dateFilter,
  ]);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const navigateToView = (row) => {
    const formType = row.raw_form_type;
    let context = "ncr";
    if (formType === "MI") context = "INSPECTION";
    else if (formType === "PC") context = "CHECKLIST";
    else if (formType === "NC") context = "ncr";
    else if (formType === "CA") context = "car";

    navigate(`/project/${projectId}/quality/${formType}/${row.doc_dns}/view`);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("이 문서를 삭제할까요?")) return;
    await deleteQualityMeta({ dns: row.doc_dns });
    setRows((prev) => prev.filter((r) => r.doc_dns !== row.doc_dns));
    setCount((c) => c - 1);
  };

  const displayBoardName =
    normalized === "ALL" ? "품질 게시판" : LABELS[normalized] + " 게시판";

  // -------------------- 컬럼 정의 --------------------
  const columns = [
    {
      key: "title",
      header: "제목",
      render: (r) => <div className="truncate max-w-[480px]">{r.title}</div>,
    },
    {
      key: "doc_type",
      header: "유형",
      render: (r) => (
        <span className="rounded-md border px-2 py-[2px] text-xs">
          {LABELS[r.doc_type]}
        </span>
      ),
    },
    {
      key: "status",
      header: "상태",
      render: (r) => (
        <span
          className={`px-2 py-0.5 text-xs rounded-full ${statusColor(
            r.status
          )}`}
        >
          {statusText(r.status)}
        </span>
      ),
    },
    {
      key: "writer",
      header: "작성자",
      render: (r) => r.created_by_name,
    },
    {
      key: "created_at",
      header: "작성일",
      render: (r) => formatDateTime(r.created_at),
    },
    // {
    //   key: "result",
    //   header: "결과",
    //   render: (r) =>
    //     ({
    //       pass: "합격",
    //       fail: "불합격",
    //       "": "미입력",
    //     }[r.result] ??
    //     r.result ??
    //     "-"),
    // },
    {
      key: "actions",
      header: "관리",
      align: "center",
      headerAlign: "center",
      render: (row) => (
        <button
          className="border rounded-lg px-2 py-1 text-xs hover:bg-gray-50"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(row);
          }}
        >
          삭제
        </button>
      ),
    },
  ];

  // -------------------- UI --------------------
  return (
    <div className="p-6">
      <PageHeader>{displayBoardName}</PageHeader>

      <BoardToolbar
        left={null}
        right={
          <>
            <div className="mr-3">
              <SearchBar
                q={q}
                onChangeQ={setQ}
                onSearch={applySearch}
                searchField={searchField}
                onChangeSearchField={setSearchField}
                searchFieldOptions={SEARCH_FIELDS}
                dateFilter={dateFilter}
                onChangeDateFilter={setDateFilter}
                startDate={startDate}
                endDate={endDate}
                onDateChange={({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                pageSize={pageSize}
                onChangePageSize={setPageSize}
              />
            </div>
          </>
        }
      />

      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        keyField="doc_dns"
        onRowClick={navigateToView}
      />

      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={count}
        onPageChange={setPage}
      />
    </div>
  );
}
